import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { openRazorpayCheckout, cancelSubscription } from '../../services/subscriptionService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';
import UpgradeSuccessModal from '../../components/ui/UpgradeSuccessModal';

const MONTHLY_PLAN_ID = import.meta.env?.VITE_RAZORPAY_MONTHLY_PLAN_ID || '';
const ANNUAL_PLAN_ID = import.meta.env?.VITE_RAZORPAY_ANNUAL_PLAN_ID || '';

const PricingPage = () => {
  const [isAnnual, setIsAnnual] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { user, subscription, refreshUsage } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const isPro = subscription?.tier === 'pro' || subscription?.tier === 'enterprise';
  const isCancelled = subscription?.status === 'cancelled' || subscription?.status === 'expired';

  const handleUpgrade = async () => {
    if (!user) { toast.error('Please sign in first.'); return; }
    const planId = isAnnual ? ANNUAL_PLAN_ID : MONTHLY_PLAN_ID;
    if (!planId) { toast.error('Payment is not configured yet.'); return; }
    setLoading(true);
    try {
      const result = await openRazorpayCheckout(planId, {
        name: user?.user_metadata?.full_name || '',
        email: user?.email || '',
      });
      if (result?.success) {
        await refreshUsage?.();
        setShowSuccessModal(true);
      }
    } catch (err) {
      if (err?.message !== 'Payment cancelled') toast.error(err?.message || 'Payment failed.');
    } finally { setLoading(false); }
  };

  const handleCancel = async () => {
    if (!confirm('Cancel your Pro subscription?\n\nYou\'ll keep Pro access until the end of your billing period.')) return;
    setLoading(true);
    try {
      await cancelSubscription();
      toast.success('Subscription cancelled. Pro access continues until end of billing period.');
      refreshUsage?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to cancel.');
    } finally { setLoading(false); }
  };

  const features = [
    { name: 'AI Content Generation', free: '5/month', pro: '100/month' },
    { name: 'Publish to LinkedIn & X', free: '3/month', pro: 'Unlimited' },
    { name: 'Image Attachments', free: false, pro: true },
    { name: 'Custom Writing Styles', free: '2 styles', pro: 'Unlimited' },
    { name: 'Analytics Dashboard', free: false, pro: true },
    { name: 'Post Scheduling', free: false, pro: 'coming soon' },
    { name: 'Priority Support', free: false, pro: true },
    { name: 'Early Access to New Features', free: false, pro: true },
  ];

  const renderActionButton = (plan) => {
    if (plan === 'free') {
      if (!isPro) {
        return (
          <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-muted border border-border rounded-lg text-xs text-muted-foreground font-medium">
            <Icon name="Check" size={12} /> Current Plan
          </span>
        );
      }
      return <span className="text-xs text-muted-foreground">Basic tier</span>;
    }

    // Pro plan
    if (isPro && !isCancelled) {
      return (
        <div className="space-y-2">
          <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-xs text-emerald-400 font-medium">
            <Icon name="Check" size={12} /> Active Pro Plan
          </span>
          <button onClick={handleCancel} disabled={loading} className="block text-xs text-muted-foreground hover:text-red-400 transition-colors disabled:opacity-50">
            {loading ? 'Processing...' : 'Cancel Subscription'}
          </button>
        </div>
      );
    }
    if (isPro && isCancelled) {
      return (
        <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-muted border border-border rounded-lg text-xs text-muted-foreground font-medium">
          Cancelled — Pro until period ends
        </span>
      );
    }
    return (
      <button onClick={handleUpgrade} disabled={loading} className="w-full py-2.5 px-4 bg-gradient-to-r from-[#7E6BC4] to-[#A78BFA] hover:opacity-90 text-white font-semibold text-sm rounded-lg transition-all disabled:opacity-50">
        {loading ? 'Processing...' : `Upgrade — ₹${isAnnual ? '4,190/year' : '419/month'}`}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <UpgradeSuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate('/idea-refiner');
        }}
      />
      <main className="px-4 md:px-6 lg:px-8 pb-12 flex flex-col min-h-[calc(100vh-64px)]">
        <div className="max-w-4xl mx-auto w-full flex-1 flex flex-col justify-center">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
              Compare Plans
            </h1>
            <p className="text-sm text-muted-foreground">
              Start free, upgrade when you need more. No hidden fees.
            </p>

            {/* Toggle */}
            <div className="flex items-center justify-center gap-3 mt-6">
              <span className={`text-sm ${!isAnnual ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>Monthly</span>
              <button
                onClick={() => setIsAnnual(!isAnnual)}
                className={`relative w-11 h-6 rounded-full transition-colors ${isAnnual ? 'bg-violet-500' : 'bg-muted'}`}
              >
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${isAnnual ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
              <span className={`text-sm ${isAnnual ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                Annual <span className="text-xs text-emerald-400 font-medium ml-1">Save 31%</span>
              </span>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden mb-12">
            {/* Table Header — plan names + pricing + CTA */}
            <div className="grid grid-cols-3 border-b border-border">
              {/* Features column header */}
              <div className="p-5 flex items-end">
                <p className="text-xs text-muted-foreground">Feature availability may vary. Check FAQ for details.</p>
              </div>

              {/* Basic */}
              <div className="p-5 border-l border-border">
                <h3 className="text-lg font-semibold text-foreground mb-0.5">Basic</h3>
                <div className="mb-3">
                  <span className="text-2xl font-bold text-foreground">$0</span>
                  <span className="text-muted-foreground text-sm ml-1">/month</span>
                </div>
                {renderActionButton('free')}
              </div>

              {/* Pro */}
              <div className="p-5 border-l border-violet-500/30 bg-violet-500/5 relative">
                <div className="absolute -top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#7E6BC4] to-[#A78BFA]" />
                <div className="flex items-center gap-2 mb-0.5">
                  <h3 className="text-lg font-semibold text-foreground">Pro</h3>
                  <span className="bg-gradient-to-r from-[#7E6BC4] to-[#A78BFA] text-white text-[9px] font-semibold px-2 py-0.5 rounded-full">
                    Popular
                  </span>
                </div>
                <div className="mb-3">
                  <span className="text-2xl font-bold text-foreground">₹{isAnnual ? '349' : '419'}</span>
                  <span className="text-muted-foreground text-sm ml-1">/month</span>
                  {isAnnual && <span className="text-muted-foreground text-sm ml-2 line-through">₹419</span>}
                  <p className="text-[10px] text-muted-foreground mt-0.5">≈ ${isAnnual ? '4.17' : '5'}/month</p>
                </div>
                {renderActionButton('pro')}
              </div>
            </div>

            {/* Feature Rows */}
            {features.map((f, i) => (
              <div key={i} className={`grid grid-cols-3 ${i < features.length - 1 ? 'border-b border-border' : ''}`}>
                {/* Feature name */}
                <div className="px-5 py-3.5 flex items-center">
                  <span className="text-sm text-foreground font-medium">{f.name}</span>
                </div>

                {/* Basic value */}
                <div className="px-5 py-3.5 border-l border-border flex items-center justify-center">
                  {f.free === true ? (
                    <Icon name="Check" size={16} className="text-emerald-500" />
                  ) : f.free === false ? (
                    <Icon name="Minus" size={16} className="text-muted-foreground" />
                  ) : (
                    <span className="text-sm text-muted-foreground">{f.free}</span>
                  )}
                </div>

                {/* Pro value */}
                <div className="px-5 py-3.5 border-l border-violet-500/30 bg-violet-500/5 flex items-center justify-center">
                  {f.pro === true ? (
                    <Icon name="Check" size={16} className="text-violet-400" />
                  ) : f.pro === 'coming soon' ? (
                    <span className="text-xs text-violet-400/60">Coming soon</span>
                  ) : (
                    <span className="text-sm text-foreground font-medium">{f.pro}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* FAQ */}
          <div className="max-w-2xl mx-auto">
            <h2 className="text-sm font-semibold text-foreground text-center mb-5">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {[
                { q: 'Can I try Pro features before paying?', a: 'The Basic plan gives you 5 AI generations and 3 publishes per month — enough to experience the full workflow.' },
                { q: 'Can I cancel anytime?', a: 'Yes. Cancel from your profile settings anytime. You keep Pro access until the end of your billing period.' },
                { q: 'What happens when my free quota resets?', a: 'Usage resets on the 1st of each month. You get a fresh batch of free generations and publishes.' },
                { q: 'What payment methods are accepted?', a: 'All major credit/debit cards, UPI, net banking, and wallets via Razorpay.' },
                { q: 'Do you offer refunds?', a: 'Yes. Contact us within 7 days of purchase for a full refund.' },
              ].map((item, i) => (
                <div key={i} className="bg-card border border-border rounded-xl p-4">
                  <h4 className="text-sm font-medium text-foreground mb-1">{item.q}</h4>
                  <p className="text-xs text-muted-foreground">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PricingPage;
